/**
 * Phone login and cloud saves for a page (docs/design/phone-login-admin.md): one game's page (its map opens it) or
 * the launcher (every game). Optional, never a gate: when the API offers no phone login (`GET /v1/features`; an older
 * API's 404 counts as nothing) or cannot be reached, the state is "off", nothing is shown and play is exactly as before.
 *
 * A guest's progress never leaves the device (H10: minimum data); saves sync only for a phone account. A page opens a
 * session only when this device already has an identity (a stored installation ID) or the browser carries the
 * member hint (`ej_member=1`, set by the API at a web phone login so the other game subdomains know); otherwise it
 * asks the features route and nothing else until the player starts the login. No DOM here (the dialog is
 * `accountDialog.ts`), so the flow is tested in Node with a fake client.
 *
 * Telemetry: `account_link_started`, `account_link_completed` {switchedAccount}, `whatsapp_optin_changed` — never the
 * phone number or the code (H10).
 */
import { PlatformClient, type PhoneStepResult } from "@enjoyment/platform-client";
import { MEMBER_HINT_COOKIE, type FeaturesResponse, type GameId, type InstallationPlatform, type PhoneStartResponse, type PhoneVerifyResponse, type Viewer } from "@enjoyment/contracts";
import { syncSaves, type SaveClient, type SaveStorage } from "./cloudSave";
import { apiPhone } from "./phone";
import { onProgressStored } from "./progress";

export const DEFAULT_API_URL = "https://api.enjoyment.app";

export type TrackFn = (name: string, props: Record<string, string | number | boolean>) => void;

/** The part of PlatformClient this module uses. */
export interface AccountClient extends SaveClient {
  ensureGuestSession(): Promise<{ viewer: Viewer } | null>;
  features(): Promise<FeaturesResponse | null>;
  hasStoredInstallation(): boolean;
  startPhoneLogin(phone: string, lang?: "tr" | "en"): Promise<PhoneStepResult<PhoneStartResponse>>;
  verifyPhoneLogin(input: { phone: string; code: string; whatsappOptIn: boolean }): Promise<PhoneStepResult<PhoneVerifyResponse>>;
  setWhatsappConsent(granted: boolean): Promise<Viewer | null>;
  logout(): Promise<boolean>;
  deleteAccount(): Promise<boolean>;
  clearLocalInstallation(): void;
}

/** off: nothing offered here (flag off, offline, a bot run); guest: the login is offered; member: a phone is linked. */
export type AccountStatus = "off" | "checking" | "guest" | "member";
export type SyncStatus = "idle" | "syncing" | "saved" | "offline";

export interface AccountState {
  status: AccountStatus;
  viewer: Viewer | null;
  /** The API offers phone login (its own flag); the "Kaydet" entry shows only then. */
  phoneLogin: boolean;
  sync: SyncStatus;
}

export interface AccountSetup {
  /** The games whose saves this page keeps in sync: the one being played, or all of them (launcher). */
  games: readonly GameId[];
  /** Where the dialog was opened from, for telemetry. */
  surface: "game" | "launcher";
  track?: TrackFn;
  client?: AccountClient;
  storage?: SaveStorage;
  /** False keeps everything off (bot/smoke runs without `?account=1`). */
  enabled?: boolean;
  /** Whether the browser carries the member hint cookie (`ej_member=1`); the page reads `document.cookie`. */
  memberHint?: () => boolean;
  /** Debounce after a level ends before the save is pushed. */
  syncDelayMs?: number;
}

type Listener = (state: AccountState) => void;

export class Account {
  private state: AccountState = { status: "off", viewer: null, phoneLogin: false, sync: "idle" };
  private readonly listeners = new Set<Listener>();
  private readonly savesListeners = new Set<(games: string[]) => void>();
  private starting?: Promise<AccountState>;
  private lastStart = 0;
  /** Asked once per page; a null answer (offline) is asked again on a later start. */
  private features?: FeaturesResponse;
  private syncing?: Promise<void>;
  private syncAgain = false;
  private timer?: ReturnType<typeof setTimeout>;
  private readonly track: TrackFn;

  constructor(private readonly setup: AccountSetup & { client: AccountClient; storage: SaveStorage }) {
    this.track = setup.track ?? (() => undefined);
    if (setup.enabled !== false) {
      // A level won or skipped: a member's save goes up (a guest's never does).
      onProgressStored((id) => {
        if (this.setup.games.includes(id as GameId)) this.requestSync();
      });
    }
  }

  get current(): AccountState {
    return this.state;
  }

  get games(): readonly GameId[] {
    return this.setup.games;
  }

  /** The PlatformClient behind it (the launcher's "Verilerimi sil" erases the account through it). */
  get client(): AccountClient {
    return this.setup.client;
  }

  /** Listen to state changes; returns the unsubscribe. The listener is called at once with the current state. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => void this.listeners.delete(listener);
  }

  /** After a sync changed saves on this device (another device's progress came in): the map redraws. */
  onSavesChanged(listener: (games: string[]) => void): () => void {
    this.savesListeners.add(listener);
    return () => void this.savesListeners.delete(listener);
  }

  private set(patch: Partial<AccountState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of [...this.listeners]) listener(this.state);
  }

  private setViewer(viewer: Viewer | null): void {
    this.set({ viewer, status: viewer ? (viewer.guest ? "guest" : "member") : this.state.status, ...(viewer && !viewer.guest ? { phoneLogin: true } : {}) });
  }

  /**
   * Ask what the API offers, restore this device's session if it has an identity, and sync a member's saves. Called
   * when a map opens: repeated calls reuse the features answer and the session the client keeps in memory.
   * Off/offline is retried at most once a minute, so a map opened after every level does not knock each time.
   */
  start(now = Date.now()): Promise<AccountState> {
    if (this.setup.enabled === false) return Promise.resolve(this.state);
    if (this.starting) return this.starting;
    if (this.state.status === "off" && this.lastStart && now - this.lastStart < 60_000) return Promise.resolve(this.state);
    this.lastStart = now;
    const run = this.open().finally(() => {
      if (this.starting === run) this.starting = undefined;
    });
    this.starting = run;
    return run;
  }

  private async open(): Promise<AccountState> {
    if (!this.features) {
      if (this.state.status === "off") this.set({ status: "checking" });
      const features = await this.setup.client.features();
      if (!features) {
        this.set({ status: "off", viewer: null });
        return this.state;
      }
      this.features = features;
    }
    if (!this.features.identity || !this.features.phoneLogin) {
      this.set({ status: "off", viewer: null, phoneLogin: false });
      return this.state;
    }
    this.set({ phoneLogin: true });
    // No identity on this device and no phone account in this browser: offer the login, contact nothing more. The
    // session is created when the player asks for a code (a feature that needs it).
    if (!this.setup.client.hasStoredInstallation() && !(this.setup.memberHint?.() ?? false)) {
      if (this.state.status !== "member") this.set({ status: "guest", viewer: null });
      return this.state;
    }
    const session = await this.setup.client.ensureGuestSession();
    if (!session) {
      if (this.state.status !== "member") this.set({ status: "guest", viewer: null });
      return this.state;
    }
    this.setViewer(session.viewer);
    await this.sync();
    return this.state;
  }

  private canSync(): boolean {
    return this.state.status === "member" && this.features?.saves !== false;
  }

  /** A member's progress goes up soon (after a level ends); several calls make one round. Guests: nothing. */
  requestSync(delayMs = this.setup.syncDelayMs ?? 1500): void {
    if (this.setup.enabled === false || !this.canSync()) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.sync();
    }, delayMs);
  }

  /**
   * Pull, merge and push this page's games now — for a phone account only; a guest's progress never leaves the device.
   * One round at a time; a request during a round runs one more.
   */
  sync(): Promise<void> {
    if (!this.canSync()) return Promise.resolve();
    if (this.syncing) {
      this.syncAgain = true;
      return this.syncing;
    }
    const run = (async () => {
      do {
        this.syncAgain = false;
        if (!this.canSync()) break; // signed out meanwhile
        this.set({ sync: "syncing" });
        try {
          const result = await syncSaves(this.setup.client, this.setup.storage, this.setup.games);
          this.set({ sync: result.status === "synced" ? "saved" : "offline" });
          if (result.changed.length) for (const listener of [...this.savesListeners]) listener(result.changed);
        } catch {
          this.set({ sync: "offline" });
        }
      } while (this.syncAgain);
    })().finally(() => {
      this.syncing = undefined;
    });
    this.syncing = run;
    return run;
  }

  /** Step 1 of the login: send the code. The dialog has already asked for 13+ (H9). */
  async sendCode(phone: string, lang: "tr" | "en", whatsappOptIn: boolean, resend = false): Promise<PhoneStepResult<PhoneStartResponse>> {
    if (!resend) this.track("account_link_started", { surface: this.setup.surface, whatsapp: whatsappOptIn });
    return this.setup.client.startPhoneLogin(apiPhone(phone), lang);
  }

  /**
   * Step 2: check the code. On success this device is the phone's account (a new session when the phone already had
   * one elsewhere: `switchedAccount`), and the first sync takes this device's progress to the server: merged into that
   * account's saves and written back here.
   */
  async verifyCode(phone: string, code: string, whatsappOptIn: boolean): Promise<PhoneStepResult<PhoneVerifyResponse>> {
    const result = await this.setup.client.verifyPhoneLogin({ phone: apiPhone(phone), code, whatsappOptIn });
    if (!result.ok) return result;
    this.setViewer({ ...result.value.session.viewer, guest: false });
    this.track("account_link_completed", { surface: this.setup.surface, switchedAccount: result.value.switchedAccount, whatsapp: whatsappOptIn });
    // Not awaited: the signed-in view shows "Kaydediliyor…" while this device's progress goes to the account.
    void this.sync();
    return result;
  }

  /** The WhatsApp switch. Withdrawing costs nothing (H7). False when the answer could not be saved. */
  async setWhatsapp(granted: boolean): Promise<boolean> {
    const viewer = await this.setup.client.setWhatsappConsent(granted);
    if (!viewer) return false;
    this.setViewer(viewer);
    this.track("whatsapp_optin_changed", { surface: this.setup.surface, granted });
    return true;
  }

  /**
   * "Çıkış yap": revoke the session on the server (it also drops this browser's `ej_device` link and the member hint),
   * then forget the installation: a guest without a session again. Progress on this device stays. Only after the
   * server answered: forgetting alone would leave the browser link, which signs the next guest back in.
   */
  async logout(): Promise<boolean> {
    if (!(await this.setup.client.logout())) return false;
    this.setup.client.clearLocalInstallation();
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.set({ status: "guest", viewer: null, sync: "idle" });
    return true;
  }

  /** After "Verilerimi sil" erased the account: nothing is online until the player acts again. */
  forget(): void {
    this.set({ status: this.state.phoneLogin ? "guest" : "off", viewer: null, sync: "idle" });
  }
}

// ------------------------------------------------------------------ the page's account

interface PageEnv {
  search: string;
  hostname: string;
  envUrl?: string;
}

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\])$/;

/**
 * The API origin: `https://api.enjoyment.app`, a build's `VITE_API_URL`, or `?api=` — the last only on a page served
 * from this machine (dev, smokes). A link to the live game can never send numbers and codes elsewhere.
 */
export function apiBaseUrl(env: PageEnv): string {
  const requested = new URLSearchParams(env.search).get("api");
  if (requested && LOCAL_HOST.test(env.hostname)) {
    try {
      const url = new URL(requested);
      if (url.protocol === "http:" || url.protocol === "https:") return url.origin;
    } catch {
      /* not a URL: ignore */
    }
  }
  return env.envUrl || DEFAULT_API_URL;
}

/** Bot and smoke runs stay offline unless they ask for the account (`?account=1`). */
export function accountEnabled(search: string): boolean {
  const q = new URLSearchParams(search);
  return !q.has("bot") || q.get("account") === "1";
}

interface NativeBridge {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

/** The Capacitor app (the launcher and the games in its web view) or the web. */
export function detectPlatform(): InstallationPlatform {
  try {
    const bridge = (globalThis as { Capacitor?: NativeBridge }).Capacitor;
    if (bridge?.isNativePlatform?.()) {
      const platform = bridge.getPlatform?.();
      if (platform === "ios" || platform === "android") return platform;
    }
  } catch {
    /* no bridge */
  }
  return "web";
}

let page: Account | null = null;

/** Configure this page's account once (boot does it for a game, the launcher for every game). */
export function setupAccount(setup: AccountSetup & { lang?: string; platform?: InstallationPlatform }): Account {
  if (page) return page;
  const env: PageEnv = { search: location.search, hostname: location.hostname, envUrl: import.meta.env.VITE_API_URL as string | undefined };
  const platform = setup.platform ?? detectPlatform();
  const client =
    setup.client ??
    new PlatformClient({
      apiBaseUrl: apiBaseUrl(env),
      platform,
      locale: setup.lang,
      // One account across this browser's game subdomains (the API's `ej_device` cookie); the app uses its token.
      shareBrowserAccount: platform === "web",
    });
  page = new Account({ enabled: accountEnabled(env.search), memberHint: () => readMemberHint(safeCookie()), ...setup, client, storage: setup.storage ?? safeStorage() });
  return page;
}

/** `ej_member=1` among the page's cookies (set by the API on `.enjoyment.app` at a web phone login; no identifier). */
export function readMemberHint(cookie: string): boolean {
  return cookie.split(";").some((part) => part.trim() === `${MEMBER_HINT_COOKIE}=1`);
}

function safeCookie(): string {
  try {
    return typeof document === "undefined" ? "" : document.cookie;
  } catch {
    return "";
  }
}

/** This page's account, or null when the page never set one up (embedded scenes, tests). */
export function pageAccount(): Account | null {
  return page;
}

function safeStorage(): SaveStorage {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* storage disabled */
  }
  const memory = new Map<string, string>();
  return { getItem: (key) => memory.get(key) ?? null, setItem: (key, value) => void memory.set(key, value) };
}
